import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { ChangeDetectionStrategy, Component, computed, effect, input, output, signal, ViewEncapsulation } from '@angular/core';
import { EnpsControlComponent } from '@src/suite/common/controls/enps-control/enps-control';
import { INITIAL_TIME_STAMP } from '@src/suite/common/enums/constants';

@Component({
    selector: 'st-duration-seconds-input-field',
    templateUrl: './duration-seconds-input-field.html',
    styleUrls: ['./duration-seconds-input-field.scss'],
    encapsulation: ViewEncapsulation.None,
    // TODO This might need some manual change detection - check pls
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'st-duration-seconds-input-field',
    },
})
export class DurationSecondsInputFieldComponent extends EnpsControlComponent<string> {
    private static DURATION_FORMAT_PATTERN_SECONDS: string = '^[\\d]+$';
    onEnter = output<string>();

    type = input<'text'>('text');
    showLinks = input(false, {
        transform: coerceBooleanProperty,
    });
    textToHighlight = input<string>(null);

    inEditMode = signal(true);
    internalValue = signal<string>(null);

    constructor() {
        super();
        effect(
            () => {
                this.inEditMode.set(!this.showLinks());
                this.internalValue.set(this.value());
            },
            { allowSignalWrites: true }
        );
    }

    displayValue = computed(() => {
        return (this.value() as string) || this.placeholder() || null;
    });

    keyPressed(event) {
        if (event.keyCode === 27) {
            this.elmRef.nativeElement.querySelector('input').blur();
            this.inEditMode.set(false);
        }

        if (event.keyCode === 13 || event.key === 'Enter') {
            this.onValueChanged();
            this.onEnter.emit(this.internalValue());
        }
    }

    onNgModelChanged(value: string) {
        this.internalValue.set(value);
    }

    onValueChanged() {
        if (this.internalValue() && this.isValidDurationSecondsFormat(this.internalValue())) {
            const value = this.secondsToDuration(this.internalValue());
            this.internalValue.set(this.validateValue(value));
        } else {
            this.internalValue.set(this.validateValue(this.internalValue()));
        }

        this.changed.emit(this.internalValue());

        if (this.showLinks && !this.disabled()) {
            this.inEditMode.set(false);
        }
    }

    secondsToDuration(value) {
        let secondsValue = value;
        const hours = Math.floor(secondsValue / 3600);
        secondsValue %= 3600;
        const minutes = Math.floor(secondsValue / 60);
        const seconds = secondsValue % 60;
        const formattedHours = String(hours).padStart(2, '0');
        const formattedMinutes = String(minutes).padStart(2, '0');
        const formattedSeconds = String(seconds).padStart(2, '0');
        return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
    }

    isValidDurationSecondsFormat(value) {
        const regex = RegExp(DurationSecondsInputFieldComponent.DURATION_FORMAT_PATTERN_SECONDS);
        return regex.test(value);
    }

    validateValue(value) {
        if (!value) {
            return '';
        }
        const sectioned = value.split(':');
        if (sectioned.length < 2) {
            return INITIAL_TIME_STAMP;
        }
        let mustUpdateValue;
        // if the input does not have 2 ":" or is like "01:02:03:04:05", then reset the input
        if (sectioned.length !== 3) {
            return INITIAL_TIME_STAMP;
        }
        // if hour (hh) input is not a number or negative set it to 0 or > 23
        if (isNaN(sectioned[0]) || sectioned[0] < 0 || sectioned[0] > 59) {
            sectioned[0] = '00';
            mustUpdateValue = true;
        }
        // if minutes (mm) input is not a number or negative set it to 0
        if (isNaN(sectioned[1]) || sectioned[1] < 0) {
            sectioned[1] = '00';
            mustUpdateValue = true;
        }
        // if minutes (mm) more than 59, set it to 59
        if (sectioned[1] > 59 || sectioned[1].length > 2) {
            sectioned[1] = '59';
            mustUpdateValue = true;
        }
        // if seconds(ss) input is not a number or negative set it to 0
        if (isNaN(sectioned[2]) || sectioned[2] < 0) {
            sectioned[2] = '00';
            mustUpdateValue = true;
        }
        // if seconds (ss) more than 59, set it to 59
        if (sectioned[2] > 59 || sectioned[2].length > 2) {
            sectioned[2] = '59';
            mustUpdateValue = true;
        }
        if (mustUpdateValue) {
            return sectioned.join(':');
        }

        return value;
    }

    onBlur() {
        if (this.showLinks && !this.disabled()) {
            this.inEditMode.set(false);
        }
    }

    onFocus() {
        if (this.showLinks && !this.disabled()) {
            this.inEditMode.set(true);
        }
    }
}
